import { ChangeDetectionStrategy, Component, OnInit, SimpleChanges, ViewChild, computed, inject, input, signal } from '@angular/core';

import {
    CasinoLobbyService,
    CasinoManager,
    CasinoStylesService,
    CategoryInfo,
    ConstService,
    GameDataManager,
    GameLobbyModel,
    GameManagerService,
    GlobalTranslateComponent,
    NavCategory,
    RpFavs,
    SharedTrackingService,
    UtilityService,
} from '@casinocore/platform/core';
import { AppearanceThemeModifierDirective } from '@frontend/casino-ui';
import { CasinoTheme, CasinoThemeEnum, FeatureNames, UIElement, UIElementEnum } from '@frontend/casino-ui/shared';
import { DsSegmentedControlModule } from '@frontend/ui/segmented-control';
import { NavigationService } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';

import { CategoryiconSubtitleComponent, RecentAndGymlType } from '../categoryicon-subtitle/categoryicon-subtitle.component';
import { FeatureGrid, FeatureGridBreakPoint, FeatureGridComponent } from '../feature-grid/feature-grid.component';
import { RpFavTrackingService } from './rp-fav-toggle-widget.component-resource.service';

enum EmptyScreehMessage {
    Recent = 'RPEmptyScreenMessage',
    Favorites = 'FavEmptyScreenMessage',
}

type RPFavToggleEvent = 'recentgames' | 'favouritegames';

@Component({
    selector: 'cc-rp-fav-toggle-widget',
    templateUrl: 'rp-fav-toggle-widget.component.html',
    imports: [
        DsSegmentedControlModule,
        FeatureGridComponent,
        GlobalTranslateComponent,
        IconCustomComponent,
        AppearanceThemeModifierDirective,
        CategoryiconSubtitleComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [RpFavTrackingService],
    standalone: true,
})
export class RpFavToggleWidgetComponent implements OnInit {
    private _featureGrid: FeatureGridComponent;
    @ViewChild('featureGrid') set featureGrid(el: FeatureGridComponent | undefined) {
        if (el) {
            this._featureGrid = el;
        }
    }

    lobbyType = input.required<string>();
    categoryId = input.required<string>();
    isTouch = input.required<boolean>();
    recentGames = input<string[]>([]);
    favGames = input<string[]>([]);
    category = input<CategoryInfo>({} as CategoryInfo);
    gameContainer = input<string>('');
    headerClass = input<string>('');
    recentAndGyml = RecentAndGymlType.NotRecentAndGyml;

    private readonly casinoLobbyService = inject(CasinoLobbyService);
    private readonly gameManagerService = inject(GameManagerService);
    private readonly casinoManager = inject(CasinoManager);
    private readonly gameDataManager = inject(GameDataManager);
    private readonly casinoStylesService = inject(CasinoStylesService);
    private readonly navigationService = inject(NavigationService);
    private readonly rpFavTrackingService = inject(RpFavTrackingService);
    private readonly sharedTrackingService = inject(SharedTrackingService);
    private readonly utilityService = inject(UtilityService);

    controlOptions = [
        { name: 'recentgames', title: 'Recent' },
        { name: 'favouritegames', title: 'Favorites' },
    ];

    activeOption = signal<string>('recentgames');
    newFeatureGridArr: FeatureGrid[] = [];
    games = signal<Partial<GameLobbyModel>[]>([]);
    customGrid = signal<FeatureGridBreakPoint>({});

    lobbyBackgroundImg: string = '';
    navCategories: NavCategory[] = [];
    title: string = 'My Games';
    recentId: string = 'recentgames';

    uiElement: UIElement = UIElementEnum.SEGMENTED_CONTROL;
    featureName: FeatureNames = FeatureNames.TOGGLE_WIDGET;
    currentTheme = signal<CasinoTheme>(this.utilityService.getCurrentTheme());
    gameIds = computed(() => {
        return this.isRecentTabActive ? this.recentGames() : this.favGames();
    });

    maxGameLength = computed(() => {
        const maxLen = +(this.category().categoryInfo?.sitecoreData?.attributes?.maxgamelength || 10);
        return maxLen;
    });
    toggleWidth = computed(() => (this.isTouch() ? (this.isBwinTheme() ? '55%' : '40%') : '25%'));
    toggleMarginRight = computed(() => (this.isTouch() ? '6px' : '0'));
    seeAllArray = Array.from({ length: 6 }, (_, i) => i);
    isBwinTheme = computed(() => this.currentTheme() !== CasinoThemeEnum.Mgm4);

    constructor() {}

    ngOnInit(): void {
        this.computeGrid();
        this.title = this.category().categoryInfo?.sitecoreData?.headerTitle;
        this.navCategories = this.casinoLobbyService.getNavCategories(this.lobbyType());
        this.setTabNames();
        const imageData = this.category().categoryInfo?.sitecoreData;
        this.currentTheme.set(this.utilityService.getCurrentTheme());

        if (imageData?.fullTakeOverStaticBackgroundImage) {
            this.lobbyBackgroundImg = imageData.fullTakeOverStaticBackgroundImage.src || '';
        }
    }

    setTabNames(): void {
        this.navCategories.forEach((navCategory: NavCategory) => {
            if (navCategory.categoryid === ConstService.RECENT_GAMES_CATEGORY_ID) {
                this.controlOptions[0].title = navCategory.categoryname;
            } else if (navCategory.categoryid === ConstService.FAVOURITE_GAMES_CATEGORY_ID) {
                this.controlOptions[1].title = navCategory.categoryname;
            }
        });
    }

    private computeGrid(): void {
        const customGrid: FeatureGridBreakPoint = {};
        const attributes = this.category().categoryInfo?.sitecoreData?.attributes;
        if (attributes) {
            const { xs, sm, md, lg } = attributes;
            if (xs) customGrid.xs = +xs;
            if (sm) customGrid.sm = +sm;
            if (md) customGrid.md = +md;
            if (lg) customGrid.lg = +lg;
            this.customGrid.set(customGrid);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['recentGames'] || changes['favGames']) {
            this.setGames();
        }
    }

    optionChange(event: RPFavToggleEvent): void {
        if (this._featureGrid?.desktopGamesCarousel) {
            this._featureGrid.desktopGamesCarousel.moveTo(0);
        }
        if (this.activeOption() === event) return;
        this.activeOption.set(event);
        this.setGames();

        const eventLabel = event === RpFavs.RECENTGAMES ? RpFavs.RECENT : RpFavs.FAVOURITES;
        this.rpFavTrackingService.trackRpFavToggleEvent(eventLabel);
        this.sharedTrackingService.setMyGamesToggle(eventLabel);
    }

    private setNewFeatureGridArr(): void {
        const validGames = this.validGames;
        this.newFeatureGridArr.length = 0;

        validGames.forEach((game: GameLobbyModel) => {
            this.newFeatureGridArr.push({
                games: [game],
                maxRowSpan: game.rowSpan,
                maxColSpan: game.colSpan,
            });
        });

        if (this.games().length > this.maxGameLength()) {
            this.newFeatureGridArr.push({
                games: null,
                maxRowSpan: 1,
                maxColSpan: 1,
            });
        }
    }

    setGames(): void {
        if (this.gameIds().length > 0) {
            const gamesModel: Partial<GameLobbyModel>[] = [];

            this.gameIds()
                .slice(0, this.maxGameLength() + 1)
                .forEach((gameId: string) => {
                    const gameInfo = this.gameManagerService.getGameInfo(gameId);
                    if (!gameInfo) return;

                    const gameModelItem: Partial<GameLobbyModel> = {
                        game: gameId,
                        name: gameInfo.name,
                        provider: gameInfo.provider,
                        ownerId: gameInfo.ownerId,
                        icon: '1',
                        sticker: gameInfo?.sticker,
                        gameContainer: this.gameContainer(),
                    };

                    gamesModel.push(gameModelItem);
                });

            this.setDefaultLMTGameIcons(gamesModel);
        } else {
            this.games.set([]);
        }

        this.setNewFeatureGridArr();
    }

    private get isRecentTabActive(): boolean {
        return this.activeOption() === this.recentId;
    }

    private setDefaultLMTGameIcons(gamesModellist: Partial<GameLobbyModel>[]): void {
        this.games.set([]);

        let startTileBgMode = false;
        const disabledGames = this.casinoLobbyService.getDisabledGames();
        const currentWidth = this.casinoManager.getCurrentWidth();

        gamesModellist.forEach((gameModelItem: Partial<GameLobbyModel>) => {
            const filteredGame = disabledGames.filter((disabledGame: string) => disabledGame === gameModelItem.game);
            if (filteredGame.length === 0) {
                const gameLobbyModel: Partial<GameLobbyModel> = this.gameDataManager.setGameLobbyModel(gameModelItem, this.isTouch());
                gameLobbyModel.infoIconClass = this.casinoManager.assignGameClass(currentWidth, '1');
                gameLobbyModel.lobbyType = this.lobbyType();
                gameLobbyModel.gameContainer = this.gameContainer();
                gameLobbyModel.provider = gameModelItem.provider;
                gameLobbyModel.ownerId = gameModelItem.ownerId;

                if (this.category()?.categoryInfo) {
                    gameLobbyModel.gameTileBorderRadius = this.casinoStylesService.getBorderRadius(this.category().categoryInfo);
                    gameLobbyModel.gameTileDropShadow = this.casinoStylesService.getDropShadow(this.category().categoryInfo);
                    gameLobbyModel.bgTileMode = false;
                }

                if (!this.games().find((game: Partial<GameLobbyModel>) => game.gameId === gameLobbyModel.gameId)) {
                    this.games.update((games: Partial<GameLobbyModel>[]) => (games ? [...games, gameLobbyModel] : [gameLobbyModel]));
                }
            }

            startTileBgMode = !startTileBgMode;
        });
    }

    get validGames(): Partial<GameLobbyModel>[] {
        return this.games().slice(0, this.maxGameLength());
    }

    private get activeCategoryId(): string {
        return this.isRecentTabActive ? ConstService.RECENT_GAMES_CATEGORY_ID : ConstService.FAVOURITE_GAMES_CATEGORY_ID;
    }

    get emptyScreenMessage(): string {
        return this.isRecentTabActive ? EmptyScreehMessage.Recent : EmptyScreehMessage.Favorites;
    }

    seeAll(): void {
        const categoryId = this.activeCategoryId;
        const activeSubCategory = this.navCategories.find((el: NavCategory) => el.categoryid === categoryId);

        if (activeSubCategory?.url) {
            this.navigationService.goTo(activeSubCategory?.url);
        }
    }
}
